import React from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';

import {Panel} from '@stubhub/bamboo';
import Urgency from '@stubhub/react-urgency';

import EventMicroData from '../react-event-micro-data';

import EventDate from './components/EventDate';
import EventTickets from './components/EventTickets';
import EventTitle from './components/EventTitle';
import EventVenueInfo from './components/EventVenueInfo';
import {useEventItemLogic} from './hooks/useEventItemLogic';

import './index.scss';

const EventItem = (
  {
    event,
    type = 'default',
    eventItemDisplayOptions = {},
    showDistance,
    onClick,
    entityType,
    isRedirectModalEnabled,
    userCountryCode,
    isPriceIncludeFees,
    hideTicketBisPrice,
    showVenueCountryFlag,
  },
  context
) => {
  const {globalRegistry} = context;

  const {disableDateStamp, disableVenueAndTime, className: eventItemClassName, hideBorder} = eventItemDisplayOptions;

  const {mainNodeRef, navigateTo, handleTitleClick, eventData, pricingData, microData} = useEventItemLogic(
    event,
    eventItemDisplayOptions,
    showDistance,
    globalRegistry,
    type,
    userCountryCode,
    isPriceIncludeFees,
    hideTicketBisPrice
  );

  const {eventDate, timeDisplay, venueDisplay} = eventData;
  const {totalTickets, ticketCurrencyCode, price, isSeePrices, showPriceInfo} = pricingData;
  const {microDataToggle, microDataMarkup, jsonld} = microData;

  const content = (
    <div
      className="EventItem__Body"
      ref={mainNodeRef}
      onClick={navigateTo}
      {...microDataMarkup}
      itemScope={Boolean(microDataMarkup.itemType)}
    >
      {microDataToggle && jsonld && <EventMicroData eventJSONLD={jsonld} />}

      <div className="EventItem__Info">
        <EventDate eventDate={eventDate} disableDateStamp={disableDateStamp} timeDisplay={timeDisplay} />

        <div className="EventItem__Details">
          <div className="EventItem__EventInfo">
            <EventTitle
              name={event.name}
              webURI={event.webURI}
              isRedirectModalEnabled={isRedirectModalEnabled}
              entityType={entityType}
              venueCountry={event.venue?.country}
              onClick={onClick}
              onTitleClick={handleTitleClick}
            />

            <EventVenueInfo
              venueDisplay={venueDisplay}
              disableVenueAndTime={disableVenueAndTime}
              showVenueCountryFlag={showVenueCountryFlag}
            />

            <Urgency eventDateLocal={event.eventDateLocal} totalTickets={totalTickets} />
          </div>

          <EventTickets
            showPriceInfo={showPriceInfo}
            isSeePrices={isSeePrices}
            ticketCurrencyCode={ticketCurrencyCode}
            price={price}
          />
        </div>
      </div>
    </div>
  );

  return <Panel className={cs(eventItemClassName || 'EventItem')} content={content} border={!hideBorder} />;
};

EventItem.contextTypes = {
  globalRegistry: PropTypes.object,
};

EventItem.propTypes = {
  /**
   * hideTicketBisPrice flag
   */
  hideTicketBisPrice: PropTypes.bool,

  /**
   * Is price include fees?
   */
  isPriceIncludeFees: PropTypes.bool,

  /**
   * Event object
   */
  event: PropTypes.object,
  threshold: PropTypes.number,
  type: PropTypes.string,

  /**
   * Options Containing the Parts of the Event Items to be Hidden
   */
  eventItemDisplayOptions: PropTypes.shape({
    disableTicketsLeft: PropTypes.bool,
    disableDateStamp: PropTypes.bool,
    disableVenueAndTime: PropTypes.bool,
    disablePrice: PropTypes.bool,
  }),

  /**
   * The Class Name used to override the Styling for the component.
   */
  className: PropTypes.string,

  /**
   * Is distance shown?
   */
  showDistance: PropTypes.bool,

  /** Is redirection modal enabled? */
  isRedirectModalEnabled: PropTypes.bool,

  /** Type of entity */
  entityType: PropTypes.string,

  /**
   * On Click Event Handler
   */
  onClick: PropTypes.func,

  /**
   * User's country code
   */
  userCountryCode: PropTypes.string,

  /**
   * Show venue country flag
   */
  showVenueCountryFlag: PropTypes.bool,
};

EventItem.defaultProps = {
  eventItemDisplayOptions: {
    disableTicketsLeft: false,
    disableDateStamp: false,
    disableVenueAndTime: false,
    disablePrice: false,
  },
  className: '',
};

export default EventItem;
